import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import {
  getFirstAndLastEmiDates,
  calculateEmiAmount,
} from "../../../utils/utils";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";

const JlgRejectReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null); // State for branch name filter
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null); // State for employee name filter
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getJlgRejectReportData`,
          { params: { id } }
        );
        // console.log(
        //   "rejectReport response.data: " + JSON.stringify(response.data)
        // );
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  // Extract unique branch names and employee names for dropdown options
  const branchOptions = [
    ...new Set(filteredData.map((data) => data?.branchName)),
  ]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [
    ...new Set(filteredData.map((data) => data?.username)),
  ]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  // Automatically filter data when fromDate, toDate, or searchTerm changes
  useEffect(() => {
    let filtered = reportData;

    if (fromDate && toDate) {
      filtered = filtered.filter((item) => {
        let updatedAt;
        // Filter by Date
        if (item.branchManagerStatus === "rejected") {
          updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        } else if (item.creditOfficerStatus === "rejected") {
          updatedAt = new Date(item.creditOfficerStatusUpdatedAt);
        } else if (item.misStatus === "rejected") {
          updatedAt = new Date(item.misStatusUpdatedAt);
        } else if (item.creditManagerStatus === "rejected") {
          updatedAt = new Date(item.creditManagerStatusUpdatedAt);
        } else if (item.sanctionCommitteeStatus === "rejected") {
          updatedAt = new Date(item.sanctionCommitteeStatusUpdatedAt);
        }

        return (
          updatedAt >= new Date(fromDate) && updatedAt <= new Date(toDate)
        );
      });
    }

    if (selectedBranchFilter && selectedBranchFilter.value) {
      filtered = filtered.filter(
        (item) => item.branchName === selectedBranchFilter.value
      );
    }

    if (selectedEmployeeFilter && selectedEmployeeFilter.value) {
      filtered = filtered.filter(
        (item) => item.username === selectedEmployeeFilter.value
      );
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [
    fromDate,
    toDate,
    reportData,
    selectedBranchFilter,
    selectedEmployeeFilter,
    searchTerm,
  ]);

  // Define columns with label and render logic
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    {
      label: "Center Name",
      render: (data) => data.fk_member_details_belongsTo_center_centerId.name,
    },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) =>
        `${data.permanentDoorNo},${data.permanentStreetNo},${data.permanentPanchayat},${data.permanentTaluk},${data.permanentDistrict}-${data.permanentPincode}`,
    },
    { label: "Contact Number", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    {
      label: "Sub Category",
      render: (data) => data?.businessDetails?.natureOfBusiness ?? "N/A",
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    {
      label: "Loan Amount",
      render: (data) => data.sanctionedLoanAmountBySanctionCommittee,
    },
    {
      label: "Role",
      render: (data) => {
        if (data.branchManagerStatus === "rejected") return "Branch Manager";
        if (data.creditOfficerStatus === "rejected") return "Credit Officer";
        if (data.misStatus === "rejected") return "MIS";
        if (data.creditManagerStatus === "rejected") return "Credit Manager";
        if (data.sanctionCommitteeStatus === "rejected")
          return "Sanction Committee";
        return "Unknown Role";
      },
    },
    { label: "Status", render: (data) => "Rejected" },
    {
      label: "Reason",
      render: (data) => {
        if (data.branchManagerStatus === "rejected")
          return data.branchManagerMessage;
        if (data.creditOfficerStatus === "rejected")
          return data.creditOfficerMessage;
        if (data.misStatus === "rejected") return data.misMessage;
        if (data.creditManagerStatus === "rejected")
          return data.creditManagerMessage;
        if (data.sanctionCommitteeStatus === "rejected")
          return data.sanctionCommitteeMessage;
        return "";
      },
    },
    {
      label: "OnBoard Date",
      render: (data) =>
        new Date(data.createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
    },
    {
      label: "Opened Time",
      render: (data) =>
        new Date(data.createdAt).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    // Add more columns as needed with custom render logic
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">JLG Reject Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={3} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={3} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="RejectReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JlgRejectReport;
